import type {
	ColorSchemeListener,
	Unsubscribe,
	UserCookieOptions,
	UserCookieStorage,
} from './types';

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

class DocumentCookieStorage implements UserCookieStorage {
	read(name: string): string | null {
		if (typeof document === 'undefined') {
			return null;
		}

		const parts = ('; ' + document.cookie).split('; ' + name + '=');
		if (parts.length < 2) {
			return null;
		}

		try {
			const value = String(parts.pop() || '').split(';').shift() || '';
			return value !== '' ? decodeURIComponent(value) : null;
		} catch {
			return null;
		}
	}

	write(name: string, value: string, options: UserCookieOptions): void {
		if (typeof document === 'undefined') {
			return;
		}

		const path = options.path ?? '/';
		const maxAge = options.maxAge ?? 31536000;
		const secure = options.secure ? '; Secure' : '';

		document.cookie =
			encodeURIComponent(name) +
			'=' +
			encodeURIComponent(value) +
			'; path=' +
			encodeURIComponent(path) +
			'; max-age=' +
			String(maxAge) +
			'; SameSite=Lax' +
			secure;
	}

	clear(name: string, options: UserCookieOptions): void {
		if (typeof document === 'undefined') {
			return;
		}

		const path = options.path ?? '/';
		const secure = options.secure ? '; Secure' : '';

		document.cookie =
			encodeURIComponent(name) +
			'=; path=' +
			encodeURIComponent(path) +
			'; max-age=0; SameSite=Lax' +
			secure;
	}
}

/**
 * Visitor-side state: chosen palette cookie, OS color scheme, and local clock.
 */
export class User {
	private static singleton: User | null = null;

	private chosenPaletteKey: string | null = null;

	private cookieOptions: UserCookieOptions | null;

	private readonly cookieStorage: UserCookieStorage;

	private mediaQuery: MediaQueryList | null = null;

	private mediaListener: (() => void) | null = null;

	private readonly schemeListeners = new Set<ColorSchemeListener>();

	private mockPrefersDark: boolean | null = null;

	private mockNowMs: number | null = null;

	private constructor(
		cookieOptions: UserCookieOptions | null,
		cookieStorage: UserCookieStorage
	) {
		this.cookieOptions = cookieOptions;
		this.cookieStorage = cookieStorage;
		this.hydrateChosenPaletteKey();
		this.bindColorScheme();
	}

	static instance(cookieOptions: UserCookieOptions | null = null): User {
		if (User.singleton === null) {
			User.singleton = new User(cookieOptions, new DocumentCookieStorage());
		} else if (cookieOptions !== null) {
			User.singleton.cookieOptions = cookieOptions;
			User.singleton.hydrateChosenPaletteKey();
		}

		return User.singleton;
	}

	static reset(): void {
		User.singleton?.dispose();
		User.singleton = null;
	}

	/** Visitor-local wall clock (epoch ms). */
	nowMs(): number {
		return this.mockNowMs ?? Date.now();
	}

	/** Browser IANA timezone for user-local schedule basis. */
	localTimezone(): string | null {
		if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
			return null;
		}

		return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
	}

	getChosenPaletteKey(): string | null {
		return this.chosenPaletteKey;
	}

	setChosenPaletteKey(key: string): void {
		this.chosenPaletteKey = key;
		this.persistChosenPaletteKey(key);
	}

	clearChosenPaletteKey(): void {
		this.chosenPaletteKey = null;
		this.clearPersistedPaletteKey();
	}

	prefersDarkScheme(): boolean {
		if (this.mockPrefersDark !== null) {
			return this.mockPrefersDark;
		}

		if (this.mediaQuery) {
			return this.mediaQuery.matches;
		}

		if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
			return window.matchMedia(DARK_SCHEME_QUERY).matches;
		}

		return false;
	}

	onColorSchemeChange(listener: ColorSchemeListener): Unsubscribe {
		this.schemeListeners.add(listener);
		return () => {
			this.schemeListeners.delete(listener);
		};
	}

	dispose(): void {
		if (this.mediaQuery && this.mediaListener) {
			if (typeof this.mediaQuery.removeEventListener === 'function') {
				this.mediaQuery.removeEventListener('change', this.mediaListener);
			} else if (typeof this.mediaQuery.removeListener === 'function') {
				this.mediaQuery.removeListener(this.mediaListener);
			}
		}

		this.mediaQuery = null;
		this.mediaListener = null;
		this.schemeListeners.clear();
		this.mockPrefersDark = null;
		this.mockNowMs = null;
	}

	static setPrefersDarkMock(value: boolean | null): void {
		User.instance().mockPrefersDark = value;
	}

	static setNowMsMock(value: number | null): void {
		User.instance().mockNowMs = value;
	}

	static createForTests(
		cookieOptions: UserCookieOptions | null,
		cookieStorage: UserCookieStorage
	): User {
		User.reset();
		User.singleton = new User(cookieOptions, cookieStorage);
		return User.singleton;
	}

	private hydrateChosenPaletteKey(): void {
		const options = this.cookieOptions;
		if (!options?.name) {
			this.chosenPaletteKey = null;
			return;
		}

		this.chosenPaletteKey = this.cookieStorage.read(options.name);
	}

	private persistChosenPaletteKey(key: string): void {
		const options = this.cookieOptions;
		if (!options?.name || key === '') {
			return;
		}

		this.cookieStorage.write(options.name, key, options);
	}

	private clearPersistedPaletteKey(): void {
		const options = this.cookieOptions;
		if (!options?.name) {
			return;
		}

		this.cookieStorage.clear(options.name, options);
	}

	private bindColorScheme(): void {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return;
		}

		this.mediaQuery = window.matchMedia(DARK_SCHEME_QUERY);
		this.mediaListener = () => {
			this.notifyColorSchemeListeners();
		};

		if (typeof this.mediaQuery.addEventListener === 'function') {
			this.mediaQuery.addEventListener('change', this.mediaListener);
		} else if (typeof this.mediaQuery.addListener === 'function') {
			this.mediaQuery.addListener(this.mediaListener);
		}
	}

	private notifyColorSchemeListeners(): void {
		const prefersDark = this.prefersDarkScheme();
		for (const listener of this.schemeListeners) {
			listener(prefersDark);
		}
	}
}
